import { SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges, SimpleChanges } from '@angular/core';

import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { PopularBetsPodTypes } from '@frontend/sports/types/components/popular-bets';
import { RxFor } from '@rx-angular/template/for';

import { getMinListHeight } from '../../popular-bets-heights';
import { PopularBetsPodAddToBetslipComponent } from '../../popular-bets-pod-add-to-betslip/popular-bets-pod-add-to-betslip.component';
import { PopularBetsPodShowMoreComponent } from '../../popular-bets-pod-show-more/popular-bets-pod-show-more.component';
import { PopularBetsPodSummaryComponent } from '../../popular-bets-pod-summary/popular-bets-pod-summary.component';
import { PopularBetsExpandCollapseService } from '../../popular-bets-services/popular-bets-expand-collapse.service';
import { PopularBetsPodEventParticipantService } from '../../popular-bets-services/popular-bets-pod-event-participant.service';
import { IPodHeightConfigModel, PickSelectionType, PodEventsModel } from '../../popular-bets.models';
import { PopularBetsPodEventComponent } from '../popular-bets-pod-event/popular-bets-pod-event.component';
import { PopularBetsPodHeaderComponent } from '../popular-bets-pod-header/popular-bets-pod-header.component';

@Component({
    selector: 'ms-popular-bets-pod',
    imports: [
        RxFor,
        PopularBetsPodEventComponent,
        PopularBetsPodShowMoreComponent,
        PopularBetsPodAddToBetslipComponent,
        PopularBetsPodHeaderComponent,
        PopularBetsPodSummaryComponent,
        SlicePipe,
    ],
    templateUrl: './popular-bets-pod.component.html',
    styleUrl: './popular-bets-pod.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PopularBetsPodComponent implements OnChanges {
    @HostBinding('class') className = 'popular-bets-pod';
    @Input() isEpcotEnabled: boolean;
    @Input() pod: PodEventsModel;
    @Input() visiblePodLegs: number;
    minHeight: number;
    pickSelectionType = PickSelectionType;
    private minPodHeightConfig: IPodHeightConfigModel = {
        rows: 5,
        isEpcotEnabled: false,
        podType: PopularBetsPodTypes.Parlay,
        isAllPodAreSameType: false,
        showOptionPick: false,
    };

    constructor(
        private popularBetsExpandCollapseService: PopularBetsExpandCollapseService,
        private participantService: PopularBetsPodEventParticipantService,
    ) {}

    ngOnChanges({ pod, visiblePodLegs }: SimpleChanges): void {
        if (visiblePodLegs?.currentValue !== visiblePodLegs?.previousValue) {
            this.pod.visibleOptionsLimit = visiblePodLegs.currentValue;
        }

        if (pod && pod.currentValue !== pod.previousValue) {
            this.updateFixtureParticipants();
        }

        this.minPodHeightConfig = {
            rows: this.pod.visibleOptionsLimit,
            isEpcotEnabled: this.isEpcotEnabled,
            showOptionPick: this.pod.isOptionPickEnabled,
            podType: this.pod.podType,
            isAllPodAreSameType: this.pod.isAllPodAreSameType,
        };
        this.minHeight = getMinListHeight(this.minPodHeightConfig);
    }

    onShowMoreLessButtonToggle(expand: boolean): void {
        this.pod.expand = expand;
        this.pod.visibleOptionsLimit = expand ? this.pod.eventsCount : this.pod.defaultNoOfLegs;

        this.popularBetsExpandCollapseService.setExpandCollapse(expand, this.pod.visibleOptionsLimit);
    }

    trackByEvent(_: number, event: GridEvent): string {
        return `${event.id}-${event.optionGroups[0].options[0].id}`;
    }

    getTrackingDetails(type: string, index: number, length: number): TrackingSource {
        return {
            ...this.pod.tracking,
            betDetails: interpolateDoubleBracesString(this.pod.tracking.betDetails, { type, index, length }),
        };
    }

    updateFixtureParticipants(): void {
        this.pod.events.forEach((event) => {
            event.participants = this.participantService.mapEventWithFilteredParticipants(event);
        });
    }
}
